import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useUrlState } from "@/lib/navState";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatRupiah } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { HandCoins, Upload, CheckCircle2, XCircle, RotateCcw, FileText, Clock } from "lucide-react";

const SUBMIT_ROLES = ["Service Advisor", "Owner", "Admin", "GM", "Branch Manager", "Kepala Bengkel", "Manager"];
const STATUS_TONE = {
  APPROVED: "bg-blue-100 text-blue-700",
  POSTED: "bg-emerald-100 text-emerald-700",
  PENDING_APPROVAL: "bg-amber-100 text-amber-700",
  PAID: "bg-emerald-100 text-emerald-700",
  REJECTED: "bg-rose-100 text-rose-700",
  NEEDS_FIX: "bg-orange-100 text-orange-700",
};
const STATUS_LABEL = {
  PENDING_APPROVAL: "WAITING APPROVAL",
  APPROVED: "APPROVED · BELUM ADA DANA",
  PAID: "PAID · MENUNGGU VERIFIKASI",
  POSTED: "VERIFIED · POSTED KE KAS",
  REJECTED: "REJECTED",
  NEEDS_FIX: "NEEDS FIX",
};
const proofUrl = (proof) => proof?.storage_path ? `${API}/or-payments/file?path=${encodeURIComponent(proof.storage_path)}&auth=${localStorage.getItem("tcc_token") || ""}` : null;

export default function OrPayments() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const canSubmit = user?.role === "admin" || SUBMIT_ROLES.includes(user?.role_name);
  const [tab, setTab] = useUrlState("tab", canSubmit ? "submit" : "queue", true);
  const [rows, setRows] = useState([]);
  const [canApprove, setCanApprove] = useState(false);
  const [canVerify, setCanVerify] = useState(false);
  const [banks, setBanks] = useState([]);
  const [busy, setBusy] = useState(false);
  const empty = { license_plate: params.get("plate") || "", wo_number: params.get("wo") || "", customer_name: "", amount: "", method: "CASH", cash_bank_id: "", reference: "", note: "", proof: null };
  const [f, setF] = useState(empty);
  const [claimId] = useState(params.get("claim") || "");
  const [orInfo, setOrInfo] = useState(null);
  useEffect(() => { if (claimId) api.get(`/or-payments/by-claim/${claimId}`).then((r) => setOrInfo(r.data)).catch(() => {}); }, [claimId]);

  const load = useCallback(() => {
    api.get("/or-payments").then((r) => { setRows(r.data.rows || []); setCanApprove(r.data.can_approve); setCanVerify(r.data.can_verify); }).catch(() => {});
  }, []);
  useEffect(() => { load(); if (canSubmit) api.get("/or-payments/banks").then((r) => setBanks(r.data || [])).catch(() => {}); }, [load, canSubmit]);

  const upload = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/or-payments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((s) => ({ ...s, proof: data })); toast.success("Bukti terunggah");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload gagal"); }
  };

  const submit = async () => {
    if (!f.license_plate.trim() && !f.wo_number.trim()) { toast.error("Nopol atau No WO wajib diisi"); return; }
    if (!Number(f.amount)) { toast.error("Nominal pembayaran OR wajib diisi"); return; }
    if (claimId && orInfo && !orInfo.or_total && !Number(f.or_amount)) { toast.error("Nominal OR belum dikonfirmasi — isi nominal OR sesuai polis"); return; }
    if (!f.cash_bank_id) { toast.error("Rekening/Kas tujuan wajib dipilih"); return; }
    if (!f.proof) { toast.error("Bukti pembayaran wajib diunggah"); return; }
    setBusy(true);
    try {
      await api.post("/or-payments", { ...f, amount: Number(f.amount), or_amount: Number(f.or_amount || 0), claim_id: claimId, idempotency_key: `or_${user?.user_id}_${Date.now()}` });
      toast.success("Pengajuan OR terkirim · menunggu approval");
      setF(empty); load(); setTab("queue");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal mengirim"); }
    finally { setBusy(false); }
  };

  const act = async (id, kind) => {
    let reason = "";
    if (kind !== "approve") { reason = window.prompt(kind === "reject" ? "Alasan penolakan:" : "Catatan perbaikan bukti:") || ""; if (kind === "reject" && !reason.trim()) { toast.error("Alasan wajib"); return; } }
    try {
      await api.post(`/or-payments/${id}/${kind === "reject" ? "reject" : kind === "fix" ? "request-fix" : kind}`, ["approve", "verify", "mark-paid"].includes(kind) ? {} : { reason });
      toast.success(kind === "approve" ? "OR disetujui · saldo belum berubah" : kind === "mark-paid" ? "Dana dicatat diterima (PAID) · menunggu verifikasi" : kind === "verify" ? "Terverifikasi · POSTED ke Kas/Bank" : kind === "reject" ? "OR ditolak" : "Minta perbaikan bukti");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Gagal"); }
  };

  const pending = useMemo(() => rows.filter((r) => (canApprove && (r.status === "PENDING_APPROVAL" || r.status === "NEEDS_FIX")) || (canVerify && (r.status === "APPROVED" || (r.status === "PAID" && !r.cash_txn_id))) || (!canApprove && !canVerify && r.status === "PENDING_APPROVAL")), [rows, canApprove, canVerify]);

  const Row = ({ r, showActions }) => (
    <div data-testid="or-row" className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
      <div className="flex items-center justify-between gap-2">
        <span className="font-mono text-sm font-bold text-gray-900">{r.license_plate || "-"}</span>
        <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${STATUS_TONE[r.status] || "bg-gray-100 text-gray-600"}`}>{STATUS_LABEL[r.status] || r.status}</span>
      </div>
      <div className="mt-1 text-[12px] text-gray-600">{r.customer_name || "-"}{r.wo_number ? ` · WO ${r.wo_number}` : ""}</div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[12px]">
        <span className="font-bold text-gray-900">{formatRupiah(r.amount)}</span>
        <span className="text-gray-500">{r.method} · {r.cash_bank || "-"}</span>
        {r.reference && <span className="text-gray-400">Ref {r.reference}</span>}
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-3 text-[11px] text-gray-400">
        <span className="flex items-center gap-1"><Clock size={11} />{r.submitted_at}</span>
        <span>oleh {r.submitted_by}</span>
        {r.approved_by && <span>· approver {r.approved_by}</span>}
        {r.reject_reason && <span className="text-rose-500">· tolak: {r.reject_reason}</span>}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        {proofUrl(r.proof) && <a data-testid="or-proof-link" href={proofUrl(r.proof)} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-[12px] font-semibold text-blue-600 hover:underline"><FileText size={13} />Lihat bukti</a>}
        {showActions && canApprove && (r.status === "PENDING_APPROVAL" || r.status === "NEEDS_FIX") && (
          <div className="ml-auto flex gap-1.5">
            <Button data-testid={`or-approve-${r.id}`} size="sm" onClick={() => act(r.id, "approve")} className="h-8 bg-emerald-600 text-white hover:bg-emerald-700"><CheckCircle2 size={14} className="mr-1" />Setujui</Button>
            <Button data-testid={`or-fix-${r.id}`} size="sm" variant="outline" onClick={() => act(r.id, "fix")} className="h-8 border-orange-300 text-orange-700"><RotateCcw size={14} /></Button>
            <Button data-testid={`or-reject-${r.id}`} size="sm" variant="outline" onClick={() => act(r.id, "reject")} className="h-8 border-rose-300 text-rose-700"><XCircle size={14} /></Button>
          </div>
        )}
        {showActions && canVerify && r.status === "APPROVED" && (
          <Button data-testid={`or-mark-paid-${r.id}`} size="sm" onClick={() => act(r.id, "mark-paid")} className="ml-auto h-9 bg-[#1E3A5F] font-bold text-white"><CheckCircle2 size={14} className="mr-1" />Catat Dana Diterima (PAID)</Button>
        )}
        {showActions && canVerify && r.status === "PAID" && !r.cash_txn_id && (r.paid_marked_by !== user?.name || user?.role_name === "Owner" || user?.role === "root") && (
          <Button data-testid={`or-verify-${r.id}`} size="sm" onClick={() => act(r.id, "verify")} className="ml-auto h-9 bg-emerald-700 font-bold text-white"><CheckCircle2 size={14} className="mr-1" />Verifikasi & Posting ke Kas</Button>
        )}
        {showActions && !canApprove && (r.status === "PENDING_APPROVAL") && <span className="ml-auto text-[11px] font-semibold text-amber-700">Menunggu keputusan Finance Manager</span>}
        {showActions && r.status === "PAID" && !r.cash_txn_id && r.paid_marked_by === user?.name && user?.role_name !== "Owner" && <span data-testid={`or-wait-other-${r.id}`} className="ml-auto text-[11px] font-semibold text-blue-700">Menunggu verifikasi petugas Finance lain</span>}
      </div>
    </div>
  );

  return (
    <div className="min-h-[calc(100vh-3rem)] w-full max-w-full overflow-x-hidden" data-testid="or-payments-page">
      <div className="mx-auto min-w-0 max-w-3xl">
        <div className="mb-4 flex items-center gap-2.5">
          <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-100 text-amber-600"><HandCoins size={20} /></span>
          <div><h1 className="font-heading text-xl font-extrabold text-gray-900 sm:text-2xl">Pembayaran OR</h1><p className="text-xs text-gray-500">Pengajuan & approval biaya Own Risk · WIB</p></div>
        </div>

        <div className="mb-4 flex gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1 text-sm font-semibold">
          {canSubmit && <button data-testid="or-tab-submit" onClick={() => setTab("submit")} className={`flex-1 rounded-md px-3 py-1.5 ${tab === "submit" ? "bg-white shadow text-gray-900" : "text-gray-500"}`}>Ajukan OR</button>}
          {(canApprove || canVerify) && <button data-testid="or-tab-queue" onClick={() => setTab("queue")} className={`flex-1 rounded-md px-3 py-1.5 ${tab === "queue" ? "bg-white shadow text-gray-900" : "text-gray-500"}`}>{canApprove ? "Antrean Approval" : "Verifikasi Dana"}{pending.length ? ` (${pending.length})` : ""}</button>}
          <button data-testid="or-tab-history" onClick={() => setTab("history")} className={`flex-1 rounded-md px-3 py-1.5 ${tab === "history" ? "bg-white shadow text-gray-900" : "text-gray-500"}`}>Riwayat</button>
        </div>

        {tab === "submit" && canSubmit && (
          <div className="space-y-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm" data-testid="or-submit-form">
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Nomor Polisi</Label><Input data-testid="or-plate" value={f.license_plate} onChange={(e) => setF({ ...f, license_plate: e.target.value.toUpperCase() })} placeholder="B 1234 XX" className="mt-1" /></div>
              <div><Label className="text-xs">Nomor WO</Label><Input data-testid="or-wo" value={f.wo_number} onChange={(e) => setF({ ...f, wo_number: e.target.value })} placeholder="WO-…" className="mt-1" /></div>
            </div>
            <div><Label className="text-xs">Nama Customer</Label><Input data-testid="or-customer" value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} className="mt-1" /></div>
            {claimId && orInfo && (
              <div data-testid="or-total-info" className="rounded-lg bg-gray-50 p-2 text-xs text-gray-700">
                {orInfo.or_total
                  ? <>Nominal OR: <b>{formatRupiah(orInfo.or_total)}</b> · Sudah dibayar {formatRupiah(orInfo.or_paid)} · Sisa <b>{formatRupiah(orInfo.or_outstanding)}</b></>
                  : <><b className="text-amber-700">OR Belum Dikonfirmasi.</b> Isi nominal OR sesuai polis / konfirmasi asuransi:
                    <Input data-testid="or-total-confirm" type="number" value={f.or_amount || ""} onChange={(e) => setF({ ...f, or_amount: e.target.value })} placeholder="Nominal OR sesuai polis" className="mt-1 h-9" /></>}
              </div>
            )}
            <div>
              <Label className="text-xs">Nominal Dibayar Sekarang (boleh sebagian)</Label>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <Input data-testid="or-amount" type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} placeholder="Nominal aktual diterima" className="h-9 w-48" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><Label className="text-xs">Metode Pembayaran</Label>
                <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}><SelectTrigger data-testid="or-method" className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="CASH">Tunai</SelectItem><SelectItem value="TRANSFER">Transfer</SelectItem></SelectContent></Select>
              </div>
              <div><Label className="text-xs">{f.method === "CASH" ? "Kas Tujuan (Kas Besar/Kecil)" : "Bank Tujuan"}</Label>
                <Select value={f.cash_bank_id} onValueChange={(v) => setF({ ...f, cash_bank_id: v })}><SelectTrigger data-testid="or-bank" className="mt-1"><SelectValue placeholder="Pilih…" /></SelectTrigger><SelectContent>{banks.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}{b.bank_name ? ` · ${b.bank_name}` : ""}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div><Label className="text-xs">Nomor Referensi</Label><Input data-testid="or-reference" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className="mt-1" /></div>
            <div>
              <Label className="text-xs">Bukti Pembayaran <span className="text-red-500">*</span></Label>
              <label className="mt-1 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50">
                <Upload size={16} />{f.proof ? f.proof.original_filename : "Unggah foto/PDF bukti"}
                <input data-testid="or-proof" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
              </label>
            </div>
            <div><Label className="text-xs">Keterangan</Label><Input data-testid="or-note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="mt-1" /></div>
            <Button data-testid="or-submit" disabled={busy} onClick={submit} className="w-full bg-amber-600 font-semibold text-white hover:bg-amber-700">{busy ? "Mengirim…" : "Kirim untuk Approval"}</Button>
          </div>
        )}

        {tab === "queue" && (
          <div className="space-y-2" data-testid="or-queue">
            {pending.length === 0 ? <p className="py-8 text-center text-sm text-gray-400">{canApprove ? "Tidak ada pengajuan menunggu approval." : "Tidak ada OR menunggu verifikasi dana."}</p> : pending.map((r) => <Row key={r.id} r={r} showActions />)}
          </div>
        )}

        {tab === "history" && (
          <div className="space-y-2" data-testid="or-history">
            {rows.length === 0 ? <p className="py-8 text-center text-sm text-gray-400">Belum ada pengajuan OR.</p> : rows.map((r) => <Row key={r.id} r={r} />)}
          </div>
        )}
      </div>
    </div>
  );
}
